import { ENTITLEMENT } from './chunk-AG5LXGLQ.js';
import { TX_TENANCY_SCOPES } from './chunk-BQW7ZP4S.js';
import { canonicalRung } from './chunk-INX63VAT.js';

// src/attribution/scope.ts
var RECORD_RESOURCES = [
  "watch",
  "tracer",
  "alertRule",
  "groupingRule",
  "pipeline",
  "savedView"
];
function isRecordResource(resource) {
  return RECORD_RESOURCES.includes(resource);
}
var WRITE_PATH = {
  transaction: "none",
  address: "overlay",
  entity: "overlay",
  label: "overlay",
  tag: "overlay",
  group: "overlay",
  // A rung and nothing else — no sharing bit, no team carriers — which is the
  // registry's shape: `private` lands on `user`, the rest on their own rung.
  primary: "registry",
  protocol: "registry",
  actor: "none",
  situation: "none",
  watch: "record",
  tracer: "record",
  alertRule: "record",
  groupingRule: "record",
  pipeline: "record",
  savedView: "record"
};
var PHASE_1_RESOLVE_SCOPES = ["USER", "TENANT", "GLOBAL"];
var PHASE_1_WRITE_SCOPES = ["private", "team", "tenant"];
var OVERLAY_WRITE_SCOPES = ["private", "team", "tenant", "org", "customer"];
var PHASE_1_DEFAULT_WRITE_SCOPE = "tenant";
var PHASE_1_DEFAULT_RESOLVE_SCOPE = "USER";
function resourcePolicy(overrides = {}) {
  return {
    enabled: true,
    resolvedView: true,
    showProvenance: true,
    showLogos: true,
    resolveScopes: PHASE_1_RESOLVE_SCOPES,
    writeScopes: PHASE_1_WRITE_SCOPES,
    defaultWriteScope: PHASE_1_DEFAULT_WRITE_SCOPE,
    defaultResolveScope: PHASE_1_DEFAULT_RESOLVE_SCOPE,
    configurableWriteDefault: true,
    configurableReadDefault: true,
    ...overrides
  };
}
var DISABLED_RESOURCE_POLICY = {
  enabled: false,
  resolvedView: false,
  showProvenance: false,
  showLogos: false,
  resolveScopes: [],
  writeScopes: ["private"],
  defaultWriteScope: "private",
  defaultResolveScope: "USER",
  configurableWriteDefault: false,
  configurableReadDefault: false
};
function attributionPolicies(overrides = {}) {
  const base = {
    // Transactions already receive a fully-resolved payload (with alternatives)
    // from `transactions-mgmt-api`, so the widen ladder is safe to show in full.
    // The WRITE axis still takes no saved default: the patch route carries no
    // ABAC fields, so a transaction overlay is tenant-wide by construction and
    // there is nothing to choose. The READ axis does, now that the batch resolve
    // takes a tier — while it could not, a saved default would have applied to a
    // transaction's detail page and not to the same transaction's row in a table.
    transaction: resourcePolicy({
      configurableWriteDefault: false,
      configurableReadDefault: true
    }),
    // The catalog overlays reach the whole ladder: an attachment can be written
    // for the org or the customer (`rung` on the wire), which the backend lets
    // only a caller with the catalog promote grant do, and only for a row at
    // least that wide. `offeredWriteScopes` narrows the menu by the plan's
    // ladder and that grant, so a caller without either never sees the rungs.
    address: resourcePolicy({ writeScopes: OVERLAY_WRITE_SCOPES }),
    entity: resourcePolicy({ writeScopes: OVERLAY_WRITE_SCOPES }),
    label: resourcePolicy({ writeScopes: OVERLAY_WRITE_SCOPES }),
    tag: resourcePolicy({ writeScopes: OVERLAY_WRITE_SCOPES }),
    group: resourcePolicy({ writeScopes: OVERLAY_WRITE_SCOPES }),
    // Where a new primary choice is written: the reader's own rung, or a shared
    // one for everyone below it. Four rungs and no `team`, because a team is a
    // carrier on a row and not a rung, and a primary row carries none — a
    // "team primary" would be written at the tenant and read as one.
    //
    // `private` by default: choosing what an address leads with is a personal
    // reading until somebody deliberately makes it the tenant's. Org and
    // customer need the catalog promote grant, as every catalog write at a
    // shared rung does (`offeredWriteScopes` narrows them away without it).
    //
    // No read axis. The reader always sees the MOST SPECIFIC rung's choice —
    // there is no "read the primaries as my tenant sees them" — so the read card
    // never lists it and the admin policy editor offers only the write row.
    primary: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      showProvenance: false,
      showLogos: false,
      defaultWriteScope: "private",
      writeScopes: ["private", "tenant", "org", "customer"],
      writeCapability: ENTITLEMENT.CATALOG_ADVANCED
    }),
    // Warehouse reads use the same saved-default ladder. Actors have no write
    // surface; their totals and counts resolve one transaction copy per tier.
    actor: resourcePolicy({
      resolveScopes: ["USER", "TENANT", "ORG", "CUSTOMER", "GLOBAL"],
      configurableWriteDefault: false,
      readCapability: ENTITLEMENT.ACTORS,
      readHint: "Tier used for actor rankings, launch counts and findings",
      readKind: "data",
      showProvenance: false,
      showLogos: false
    }),
    watch: resourcePolicy({
      resolveScopes: ["USER", "TENANT", "ORG", "CUSTOMER", "GLOBAL"],
      defaultResolveScope: "USER",
      defaultWriteScope: "private",
      writeScopes: ["private", "team", "tenant", "org", "customer"],
      writeCapability: ENTITLEMENT.TRIGGERS,
      readHint: "Watches visible at this tier; does not change monitoring or recipients",
      readKind: "data",
      showProvenance: false,
      showLogos: false
    }),
    situation: resourcePolicy({
      resolveScopes: ["USER", "TENANT", "ORG", "CUSTOMER", "GLOBAL"],
      defaultResolveScope: "TENANT",
      configurableWriteDefault: false,
      readCapability: ENTITLEMENT.SITUATIONS,
      readHint: "Tier used for detected situations and their details",
      readKind: "data",
      showProvenance: false,
      showLogos: false
    }),
    // Protocol definitions are shared decoding machinery, so the two personal
    // rungs are deliberately absent: `private` / `team` would mean "decode this
    // protocol differently for just me", which makes one tenant's transactions
    // irreproducible for their colleagues. Tenant-wide is the narrowest rung
    // that keeps a decode explainable to everyone who reads it, and it stays the
    // default.
    //
    // The two rungs ABOVE it are offered, and were missing rather than excluded:
    // the reasoning above rules out going narrower than a tenant and says
    // nothing about going wider, while the backend has always accepted
    // `customer` / `org` / `tenant` / `user` and the store folds all four. A
    // customer running several orgs on one decoder had to author the same patch
    // once per tenant and keep them in step by hand — which is the drift the
    // ladder exists to remove.
    //
    // `system` is still absent, and that is a real exclusion: the system rung is
    // a published definition rather than an overlay, and the API refuses a patch
    // there (`POST /protocols/publish` is its own internal-only door).
    protocol: resourcePolicy({
      writeScopes: ["tenant", "org", "customer"],
      defaultWriteScope: "tenant",
      configurableWriteDefault: false,
      configurableReadDefault: false
    }),
    // -- The authored records (see RECORD_RESOURCES) ----------------------
    //
    // Every default below is what the surface ALREADY did before it was asked
    // to consult this table, and deliberately so: extending the setting must
    // give people a choice, not silently move anyone's rows. That is why these
    // five do not share one default — a grouping rule has always been
    // tenant-wide and a tracer has always been owner-only, and unifying them
    // here would have re-homed live rows on deploy.
    //
    // None carries a read axis: there is no precedence stack to resolve, so
    // `resolvedView` is off and the read card never lists them.
    // Rung on the row + team carriers, so the whole ladder is expressible.
    tracer: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      defaultWriteScope: "private",
      writeScopes: ["private", "team", "tenant", "org", "customer"]
    }),
    // Was narrowed to `["private", "team"]` on the grounds that "an alert rule
    // has NO tenancy rung on the wire". That was a statement about this app's
    // adapter, not about the route, and it was circular: `TriggerRuleRow.scope`
    // has always existed and `TriggerRuleCreate` has always accepted it —
    // `ruleToWire` simply never sent one. Checked against the deployed route
    // 2026-09-23: a rule POSTed at `tenant` comes back at `tenant` with
    // `owner_user_id: null` and is then readable by every user in the tenant,
    // while another user's `user`-scoped rule stays invisible to them.
    //
    // So it is a record like a tracer, with the same ladder, and the narrow
    // offering was the bug: the one setting that decides who can see an alert
    // could not be set, and the workspace default for it was read and dropped.
    alertRule: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      defaultWriteScope: "private",
      writeScopes: ["private", "team", "tenant", "org", "customer"]
    }),
    // No `team`, and that is a wire fact rather than a preference: `toWire`
    // sends `scope` and nothing else about audience, so a team choice would be
    // dropped in the adapter. A `team` rung was offered on this resource once
    // and `_applies_to` narrowed only `user` rows — a "team" rule was in force
    // tenant-wide while ranking above the tenant rule it shadowed. It is not
    // coming back through a default.
    groupingRule: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      defaultWriteScope: "tenant",
      writeScopes: ["private", "tenant", "org", "customer"]
    }),
    // Same shape: `save` posts a `scope` and no carrier.
    pipeline: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      defaultWriteScope: "private",
      writeScopes: ["private", "tenant", "org", "customer"]
    }),
    // `org` / `customer` are real rungs on the backend enum and `_guard_scope`
    // admits them with the write verb, but the save dialog has only ever
    // offered Personal and Shared. Kept to what the product offers rather than
    // to what the wire accepts: widening is one line here the day the dialog
    // grows the options, and offering a default nobody can pick at attach time
    // is the asymmetry this table exists to avoid.
    // Personal and Shared, the two the save dialog offers. No `team` even
    // though `ViewScope` has the rung: `viewToWire` sends no `visible_to_team_ids`,
    // and the backend's tier scopes (which carry their audience in the tenancy
    // tuple) exclude TEAM — so a team-scoped view would depend on carriers this
    // client never writes. `org` / `customer` are real and admitted with the
    // write verb; they wait for the dialog to offer them.
    savedView: resourcePolicy({
      resolvedView: false,
      configurableReadDefault: false,
      defaultWriteScope: "private",
      writeScopes: ["private", "tenant"]
    })
  };
  const out = { ...base };
  for (const [resource, override] of Object.entries(overrides)) {
    const key = resource;
    if (override) out[key] = { ...out[key] ?? DISABLED_RESOURCE_POLICY, ...override };
  }
  return out;
}
var ATTRIBUTION_POLICIES = attributionPolicies();
function policyFor(resource, policies = ATTRIBUTION_POLICIES) {
  return policies[resource] ?? DISABLED_RESOURCE_POLICY;
}
function resolveWriteScope(overrides, resource, policies = ATTRIBUTION_POLICIES) {
  const policy = policyFor(resource, policies);
  const override = overrides?.[resource];
  if (override && policy.writeScopes.includes(override)) return override;
  return policy.defaultWriteScope;
}
function resolveReadScope(overrides, resource, policies = ATTRIBUTION_POLICIES) {
  const policy = policyFor(resource, policies);
  const override = overrides?.[resource];
  if (override && policy.resolveScopes.includes(override)) return override;
  return policy.defaultResolveScope;
}
function writeScopeResources(policies = ATTRIBUTION_POLICIES) {
  return Object.keys(policies).filter(
    (resource) => policies[resource].enabled && policies[resource].configurableWriteDefault
  );
}
function readScopeResources(policies = ATTRIBUTION_POLICIES) {
  return Object.keys(policies).filter(
    (resource) => policies[resource].enabled && policies[resource].resolvedView && policies[resource].configurableReadDefault
  );
}
var RECORD_TIER_TO_READ_SCOPE = {
  user: "USER",
  tenant: "TENANT",
  org: "ORG",
  customer: "CUSTOMER",
  global: "GLOBAL"
};
var WRITE_SCOPE_TO_ANALYTICS = {
  private: "user",
  team: "user",
  tenant: "tenant",
  org: "org",
  customer: "customer"
};
var READ_SCOPE_TO_ANALYTICS = {
  USER: "merged",
  TENANT: "tenant",
  ORG: "org",
  CUSTOMER: "customer",
  GLOBAL: "system"
};
function attributionLadderGap(policies = ATTRIBUTION_POLICIES) {
  const offered = new Set(
    readScopeResources(policies).filter((resource) => policies[resource].readKind !== "data").flatMap((resource) => policies[resource].resolveScopes)
  );
  return TX_TENANCY_SCOPES.map((tier) => tier.id).filter(
    (tier) => !offered.has(RECORD_TIER_TO_READ_SCOPE[tier])
  );
}
var WRITE_SCOPE_RUNG = {
  private: "user",
  team: null,
  tenant: "tenant",
  org: "org",
  customer: "customer"
};
function primaryWriteRung(scope) {
  const rung = WRITE_SCOPE_RUNG[scope];
  return rung && rung !== "global" ? rung : "user";
}
function readScopeRung(scope) {
  return canonicalRung(scope);
}
var WRITE_SCOPE_LABEL = {
  private: "Private to me",
  team: "Specific teams",
  tenant: "Whole tenant",
  org: "Whole organization",
  customer: "Whole customer"
};
var RECORD_RUNG_LABEL = {
  user: "Private",
  team: "Team",
  tenant: "Tenant",
  org: "Org",
  customer: "Company",
  system: "Platform"
};
function recordRungLabel(scope) {
  return scope && scope in RECORD_RUNG_LABEL ? RECORD_RUNG_LABEL[scope] : null;
}
var WORKSPACE_WRITE_SCOPE_LABEL = {
  private: "Private to me",
  team: "Specific teams",
  tenant: "My workspace",
  org: "My workspace",
  customer: "My workspace"
};
var WORKSPACE_RESOLVE_SCOPE_LABEL = {
  USER: "Your view",
  TENANT: "Workspace",
  ORG: "Workspace",
  CUSTOMER: "Workspace",
  GLOBAL: "Everywhere"
};
function writeScopeLabel(scope, shared) {
  return (shared ? WRITE_SCOPE_LABEL : WORKSPACE_WRITE_SCOPE_LABEL)[scope];
}
function resolveScopeLabel(scope, shared) {
  return (shared ? RESOLVE_SCOPE_LABEL : WORKSPACE_RESOLVE_SCOPE_LABEL)[scope];
}
var WRITE_SCOPE_MEANING = {
  private: "Only you can see it \u2014 teammates opening this address will not.",
  team: "Your teams can see it. Everyone else in the tenant will not.",
  tenant: "Everyone in your tenant can see it.",
  org: "Shared across the organization.",
  customer: "Shared across the whole account."
};
var RESOLVE_SCOPE_LABEL = {
  USER: "Your view",
  TENANT: "Tenant",
  ORG: "Organization",
  CUSTOMER: "Customer",
  GLOBAL: "Global"
};
function tierWeightToScope(tierWeight) {
  if (tierWeight >= 60) return "user";
  if (tierWeight >= 40) return "tenant";
  if (tierWeight > 30) return "org";
  if (tierWeight >= 30) return "default";
  if (tierWeight >= 20) return "entity";
  return "provider";
}
var SCOPE_PROVENANCE_LABEL = {
  user: "Your overlay",
  tenant: "Tenant overlay",
  org: "Organization overlay",
  default: "System default",
  entity: "Inherited from entity",
  provider: "Provider candidate"
};
function provenanceLabel(tierWeight) {
  return SCOPE_PROVENANCE_LABEL[tierWeightToScope(tierWeight)];
}
var SCOPE_HINT_FLAG = "ui.scopeHints";

export { ATTRIBUTION_POLICIES, DISABLED_RESOURCE_POLICY, READ_SCOPE_TO_ANALYTICS, RECORD_RESOURCES, RECORD_RUNG_LABEL, RECORD_TIER_TO_READ_SCOPE, RESOLVE_SCOPE_LABEL, SCOPE_HINT_FLAG, SCOPE_PROVENANCE_LABEL, WORKSPACE_RESOLVE_SCOPE_LABEL, WORKSPACE_WRITE_SCOPE_LABEL, WRITE_PATH, WRITE_SCOPE_LABEL, WRITE_SCOPE_MEANING, WRITE_SCOPE_RUNG, WRITE_SCOPE_TO_ANALYTICS, attributionLadderGap, attributionPolicies, isRecordResource, policyFor, primaryWriteRung, provenanceLabel, readScopeResources, readScopeRung, recordRungLabel, resolveReadScope, resolveScopeLabel, resolveWriteScope, resourcePolicy, tierWeightToScope, writeScopeLabel, writeScopeResources };
//# sourceMappingURL=chunk-B7PQN6DQ.js.map
//# sourceMappingURL=chunk-B7PQN6DQ.js.map